import { useCallback, useEffect, useRef, useState } from 'react';

export function useCanvasPanZoom(options: {
  worldWidth: number;
  worldHeight: number;
  focusX: number;
  focusY: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const dragRef = useRef<{ active: boolean; startX: number; startY: number; panX: number; panY: number } | null>(
    null,
  );

  const centerOnFocus = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { clientWidth: w, clientHeight: h } = el;
    setPan({
      x: w / 2 - options.focusX,
      y: h / 2 - options.focusY,
    });
    setZoom(1);
  }, [options.focusX, options.focusY]);

  useEffect(() => {
    centerOnFocus();
    const onResize = () => centerOnFocus();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [centerOnFocus]);

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
      const nextZoom = Math.min(1.6, Math.max(0.35, zoom * factor));
      const wx = (mx - pan.x) / zoom;
      const wy = (my - pan.y) / zoom;
      setZoom(nextZoom);
      setPan({
        x: mx - wx * nextZoom,
        y: my - wy * nextZoom,
      });
    },
    [pan.x, pan.y, zoom],
  );

  const onPointerDownBackground = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 && e.button !== 1) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-workspace-window]') || target.closest('[data-donna-hub]')) return;
      dragRef.current = {
        active: true,
        startX: e.clientX,
        startY: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [pan.x, pan.y],
  );

  const onPointerMoveBackground = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d?.active) return;
    setPan({
      x: d.panX + (e.clientX - d.startX),
      y: d.panY + (e.clientY - d.startY),
    });
  }, []);

  const onPointerUpBackground = useCallback((e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  return {
    containerRef,
    pan,
    zoom,
    setPan,
    centerOnFocus,
    onWheel,
    onPointerDownBackground,
    onPointerMoveBackground,
    onPointerUpBackground,
  };
}
